import { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Printer, Download, Copy, Check, QrCode, RefreshCw, Loader2 } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import api from '../../lib/api';
import { copyToClipboard } from '../../lib/clipboard';
import { toast } from 'sonner';

const KEY_TYPES = [
  { value: 'cpf', label: 'CPF' },
  { value: 'cnpj', label: 'CNPJ' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
  { value: 'random', label: 'Chave Aleatória' },
];

const AdminQrPix = () => {
  const [pixKey, setPixKey] = useState('');
  const [keyType, setKeyType] = useState('cpf');
  const [receiverName, setReceiverName] = useState('KITS TOP ALIMENTOS');
  const [city, setCity] = useState('FEIRA DE SANTANA');
  const [fixedAmount, setFixedAmount] = useState(false);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');

  const [payload, setPayload] = useState('');
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const printRef = useRef(null);

  // Carrega settings ao montar
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/settings');
        const p = data?.payment || {};
        if (p.pix_key) setPixKey(p.pix_key);
        if (p.pix_key_type) setKeyType(p.pix_key_type);
        if (p.pix_receiver_name) setReceiverName(p.pix_receiver_name);
        const company = data?.company || {};
        if (company.address) {
          const cityMatch = company.address.split('-')[0]?.trim();
          if (cityMatch) setCity(cityMatch.toUpperCase());
        }
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  const generate = async () => {
    if (!pixKey.trim()) {
      toast.error('Informe uma chave PIX');
      return;
    }
    setGenerating(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/pix/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pix_key: pixKey.trim(),
          pix_key_type: keyType,
          receiver_name: receiverName.trim() || 'KITS TOP',
          receiver_city: city.trim() || 'BRASIL',
          amount: fixedAmount && amount ? Number(amount) : null,
          txid: '***',
          description: description.trim() || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erro ao gerar QR');
      }
      const data = await res.json();
      setPayload(data.payload);
      toast.success('QR Code PIX gerado!');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!payload) return;
    const ok = await copyToClipboard(payload);
    if (ok) {
      setCopied(true);
      toast.success('Código PIX copiado!');
      setTimeout(() => setCopied(false), 2500);
    } else {
      toast.error('Não foi possível copiar. Selecione o texto manualmente.');
    }
  };

  const handlePrint = () => {
    if (!payload) {
      toast.error('Gere o QR Code antes de imprimir');
      return;
    }
    window.print();
  };

  const handleDownloadSvg = () => {
    if (!printRef.current) return;
    const svg = printRef.current.querySelector('svg');
    if (!svg) return;
    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);
    const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `qr-pix-${Date.now()}.svg`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout title="Gerador de QR Code PIX">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #pix-print-area, #pix-print-area * { visibility: visible; }
          #pix-print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 40px;
          }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Form */}
        <Card className="rounded-2xl no-print">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              Configurar QR PIX
            </CardTitle>
            <CardDescription>
              Crie um QR Code PIX para imprimir e disponibilizar no balcão, materiais de marketing ou
              redes sociais.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo da Chave</Label>
                <Select value={keyType} onValueChange={setKeyType}>
                  <SelectTrigger data-testid="qr-key-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {KEY_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="qr-key">Chave PIX</Label>
                <Input
                  id="qr-key"
                  value={pixKey}
                  onChange={(e) => setPixKey(e.target.value)}
                  placeholder="Sua chave PIX"
                  data-testid="qr-key-input"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="qr-receiver">Nome do Recebedor</Label>
                <Input
                  id="qr-receiver"
                  value={receiverName}
                  onChange={(e) => setReceiverName(e.target.value)}
                  maxLength={25}
                  data-testid="qr-receiver-name"
                />
                <p className="text-xs text-muted-foreground">Máx. 25 caracteres</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="qr-city">Cidade</Label>
                <Input
                  id="qr-city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  maxLength={15}
                  data-testid="qr-city"
                />
                <p className="text-xs text-muted-foreground">Máx. 15 caracteres</p>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-xl border p-3">
              <div>
                <Label className="text-sm">Valor fixo</Label>
                <p className="text-xs text-muted-foreground">
                  Se desligado, o cliente digita o valor ao pagar
                </p>
              </div>
              <Switch
                checked={fixedAmount}
                onCheckedChange={setFixedAmount}
                data-testid="qr-fixed-amount-toggle"
              />
            </div>

            {fixedAmount && (
              <div className="space-y-2">
                <Label htmlFor="qr-amount">Valor (R$)</Label>
                <Input
                  id="qr-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0,00"
                  data-testid="qr-amount-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="qr-desc">Descrição (opcional)</Label>
              <Input
                id="qr-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex: Pagamento KITS TOP"
                maxLength={50}
                data-testid="qr-description"
              />
            </div>

            <Button
              onClick={generate}
              disabled={generating}
              className="w-full"
              data-testid="qr-generate-button"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              {generating ? 'Gerando...' : 'Gerar QR Code'}
            </Button>
          </CardContent>
        </Card>

        {/* Preview / Print */}
        <Card className="rounded-2xl">
          <CardHeader className="no-print">
            <CardTitle className="font-heading">Pré-visualização</CardTitle>
            <CardDescription>QR Code pronto para impressão</CardDescription>
          </CardHeader>
          <CardContent>
            {payload ? (
              <div ref={printRef} id="pix-print-area" className="bg-white">
                <div className="text-center space-y-3 max-w-sm mx-auto">
                  <h2 className="font-heading text-2xl font-bold text-slate-800">{receiverName}</h2>
                  <p className="text-sm text-slate-600">Pague com PIX • Escaneie o QR Code</p>
                  <div className="p-6 bg-white border-2 border-slate-200 rounded-2xl inline-block">
                    <QRCodeSVG value={payload} size={260} level="M" includeMargin={false} />
                  </div>
                  {fixedAmount && amount && (
                    <p className="text-2xl font-bold text-emerald-600">
                      R$ {Number(amount).toFixed(2).replace('.', ',')}
                    </p>
                  )}
                  <p className="text-xs text-slate-500 break-all px-4">
                    Chave: {pixKey}
                  </p>
                  <div className="pt-4 border-t border-slate-200">
                    <p className="text-xs text-slate-500">Powered by PIX • Banco Central do Brasil</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-16 text-center text-slate-400" data-testid="qr-empty">
                <QrCode className="w-16 h-16 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Preencha os dados e clique em &ldquo;Gerar QR Code&rdquo;</p>
              </div>
            )}
          </CardContent>
          {payload && (
            <div className="px-6 pb-6 space-y-3 no-print">
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={payload}
                  onClick={(e) => e.target.select()}
                  className="text-xs font-mono"
                  data-testid="qr-payload-output"
                />
                <Button onClick={handleCopy} variant="outline" size="icon" data-testid="qr-copy-button">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={handlePrint} variant="outline" data-testid="qr-print-button">
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir
                </Button>
                <Button onClick={handleDownloadSvg} variant="outline" data-testid="qr-download-button">
                  <Download className="w-4 h-4 mr-2" />
                  Baixar SVG
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminQrPix;
